/**
 * CreateMinutesPage.js
 *
 * Template picker for creating minutes.
 *   - Displays categorized template grid loaded from /api/template-options
 *   - "Ask Trust Assistant" card navigates to /trust-assistant for AI-assisted drafting
 *   - Deep-link ?type= auto-navigates to a template form
 *   - ?prefill_type= shows a banner pointing to Trust Assistant
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Sidebar } from '@/components/Sidebar';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { toast } from 'sonner';
import { showError } from '../utils/errors';
import { fetchWithAuth } from '@/utils/api';
import {
  ArrowLeft,
  Bot,
  Check,
  ChevronRight,
  FileText,
  Gavel,
  Sparkles,
  Users,
  DollarSign,
  PlusCircle,
  MinusCircle,
  UserPlus,
  UserCheck,
  UsersRound,
  Landmark,
  MapPin,
  HeartHandshake,
  TrendingUp,
  Banknote,
  ShieldCheck,
  CalendarCheck,
  CalendarDays,
  Wallet,
  FileEdit,
  Stamp,
  FileX,
  Home,
  Building2,
  Key,
  CalendarRange,
  Receipt,
  AlertTriangle,
  Scale,
  XCircle,
  HandCoins,
  UserMinus,
  Mail,
  ClipboardList,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants & Mappings
// ---------------------------------------------------------------------------

/** Map backend icon names to lucide-react components */
const ICONS = {
  'file-text': FileText,
  'users': Users,
  'dollar-sign': DollarSign,
  'plus-circle': PlusCircle,
  'minus-circle': MinusCircle,
  'user-plus': UserPlus,
  'user-check': UserCheck,
  'users-round': UsersRound,
  'landmark': Landmark,
  'map-pin': MapPin,
  'heart-handshake': HeartHandshake,
  'gavel': Gavel,
  'heart-pulse': HeartHandshake,
  'trending-up': TrendingUp,
  'banknote': Banknote,
  'shield-check': ShieldCheck,
  'calendar-check': CalendarCheck,
  'calendar-days': CalendarDays,
  'wallet': Wallet,
  'file-edit': FileEdit,
  'stamp': Stamp,
  'file-x': FileX,
  'home': Home,
  'building-2': Building2,
  'key': Key,
  'calendar-range': CalendarRange,
  'receipt': Receipt,
  'alert-triangle': AlertTriangle,
  'scale': Scale,
  'x-circle': XCircle,
  'hand-coins': HandCoins,
  'user-minus': UserMinus,
  'mail': Mail,
};

/** Category display order and labels for the template grid */
const CATEGORY_ORDER = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'governance', label: 'Governance' },
  { key: 'financial', label: 'Financial' },
  { key: 'assets', label: 'Assets' },
  { key: 'beneficiaries', label: 'Beneficiaries' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'administrative', label: 'Administrative' },
  { key: 'legal', label: 'Legal' },
];

/** Template types that belong to the "Recommended" section */
const RECOMMENDED_TYPES = new Set([
  'initial_trustee_meeting',
  'quarterly_review',
  'annual_review',
  'distribution_to_beneficiaries',
  'evaluate_distribution',
]);

/** Mapping from backend category key → our display categories */
const CATEGORY_MAP = {
  governance: 'governance',
  financial: 'financial',
  assets: 'assets',
  property_assets: 'assets',
  beneficiaries: 'beneficiaries',
  reviews: 'reviews',
  administrative: 'administrative',
  legal: 'legal',
  priority: 'recommended',
  basic: 'recommended',
  distributions: 'recommended',
  benevolence: 'beneficiaries',
};

/**
 * Resolution subtitles for templates that produce WHEREAS/RESOLVED text.
 * Surfacing "Resolution" language helps users understand that these templates
 * generate formal trustee resolutions, not just informal meeting notes.
 */
const RESOLUTION_SUBTITLES = {
  'initial_trustee_meeting': 'Initial Trustee Resolution & Minutes',
  'general_meeting': 'General Meeting & Resolutions',
  'acceptance_of_property': 'Accept Property into Trust',
  'disposition_of_asset': 'Resolution to Dispose of Trust Asset',
  'distribution_to_beneficiaries': 'Resolution to Distribute',
  'appointment_additional_trustee': 'Resolution to Appoint Trustee',
  'appointment_successor_trustee': 'Resolution to Appoint Successor',
  'designation_of_beneficiaries': 'Resolution to Designate Beneficiaries',
  'bank_account_authorization': 'Resolution to Open Bank Account',
  'change_of_situs': 'Resolution to Change Situs',
  'benevolence_approval': 'Resolution to Approve Assistance',
  'investment_policy': 'Resolution to Approve Investment Policy',
  'loan_authorization': 'Resolution to Authorize Loan',
  'insurance_authorization': 'Resolution to Authorize Insurance',
  'annual_review': 'Annual Review Resolution',
  'quarterly_review': 'Quarterly Review Resolution',
  'trustee_compensation': 'Resolution to Approve Compensation',
  'trustee_resignation': 'Resolution for Trustee Resignation',
  'beneficiary_request_denial': 'Resolution to Deny Request',
  'hems_distribution': 'Resolution for HEMS Distribution',
  'beneficiary_distribution_notice': 'Resolution for Distribution Notice',
  'beneficiary_loan': 'Resolution to Authorize Beneficiary Loan',
  'evaluate_distribution': 'Resolution to Evaluate Distribution',
  'trust_amendment': 'Resolution to Amend Trust',
  'power_of_attorney': 'Resolution to Authorize POA',
  'trust_termination': 'Resolution to Terminate Trust',
  'real_estate_purchase': 'Resolution to Purchase Real Estate',
  'business_interest_acquisition': 'Resolution to Acquire Business Interest',
  'real_estate_lease': 'Resolution to Lease Real Estate',
  'fiscal_year_election': 'Resolution for Fiscal Year Election',
  'tax_filing_authorization': 'Resolution to Authorize Tax Filing',
  'emergency_ratification': 'Resolution to Ratify Emergency Action',
  'conflict_of_interest': 'Resolution to Disclose Conflict',
  'bill_of_sale': 'Resolution & Bill of Sale',
  'assignment_of_personal_property': 'Resolution to Assign Property',
  'general_assignment': 'Resolution for General Assignment',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CreateMinutesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedTrust, isReadOnly } = useAuth();

  // ----- Read-only guard -----
  useEffect(() => {
    if (isReadOnly) {
      toast.error('Read-only users cannot create minutes');
      navigate('/minutes', { replace: true });
    }
  }, [isReadOnly, navigate]);

  // ----- Onboarding deep-link: auto-navigate to template when ?type= is passed -----
  const fromOnboarding = searchParams.get('from') === 'onboarding' || searchParams.get('source') === 'onboarding';
  const templateType = searchParams.get('type');

  useEffect(() => {
    if (templateType && selectedTrust?.trust_id) {
      navigate(`/minutes/template/${templateType}?from=create${fromOnboarding ? '&source=onboarding' : ''}`, { replace: true });
    }
  }, [templateType, selectedTrust?.trust_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- Money prefill: show banner pointing to Trust Assistant -----
  const prefillType = searchParams.get('prefill_type');
  const prefillAmount = searchParams.get('prefill_amount');
  const showPrefillBanner = prefillType && prefillAmount;

  // ----- Template options loaded from backend -----
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  useEffect(() => {
    if (!selectedTrust?.trust_id) return;
    let cancelled = false;
    setTemplatesLoading(true);
    fetchWithAuth(`/template-options?trust_id=${selectedTrust.trust_id}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load templates');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setTemplateOptions(data.templates || data || []);
        }
      })
      .catch((err) => {
        console.error('Error loading template options:', err);
        if (!cancelled) toast.error('Failed to load templates. Please try again. If the problem continues, contact support@trustoffice.app.');
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedTrust?.trust_id]);

  // =========================================================================
  // Template selection handler
  // =========================================================================

  const handleTemplateSelect = (type) => {
    navigate(`/minutes/template/${type}?from=create`);
  };

  // =========================================================================
  // Render helpers
  // =========================================================================

  /** Build a map of templates grouped by display category */
  const buildCategorizedTemplates = () => {
    const categorized = {};
    CATEGORY_ORDER.forEach((cat) => {
      categorized[cat.key] = [];
    });

    templateOptions.forEach((t) => {
      const backendCat = t.category || 'basic';
      const displayCat = CATEGORY_MAP[backendCat] || 'other';

      if (RECOMMENDED_TYPES.has(t.type)) {
        categorized.recommended.push(t);
      } else if (categorized[displayCat]) {
        categorized[displayCat].push(t);
      } else {
        if (!categorized.other) categorized.other = [];
        categorized.other.push(t);
      }
    });

    return categorized;
  };

  /** Render a single template card */
  const renderTemplateCard = (t) => {
    const IconComp = ICONS[t.icon] || FileText;
    const isPriority = t.priority || t.type === 'initial_trustee_meeting';

    return (
      <button
        key={t.type}
        data-testid={`template-${t.type}`}
        onClick={() => handleTemplateSelect(t.type)}
        className="card-trust overflow-visible group relative flex flex-col items-start gap-2 p-5 text-left transition-all hover:border-gold/50 hover:shadow-md"
      >
        {isPriority && (
          <span className={`badge-trust absolute top-0 right-0 bg-gold text-navy text-xs font-bold px-2 py-1 rounded-bl-sm${fromOnboarding ? ' animate-pulse' : ''}`}>
            Start Here
          </span>
        )}
        <div className="flex items-center gap-3 w-full">
          <div className={`flex-shrink-0 flex h-10 w-10 items-center justify-center rounded ${isPriority ? 'bg-gold/10 text-gold' : 'bg-navy/5 text-navy'}`}>
            <IconComp className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-sm font-semibold text-navy truncate">
              {t.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {RESOLUTION_SUBTITLES[t.type] || t.description}
            </p>
            {RESOLUTION_SUBTITLES[t.type] && (
              <p className="text-xs text-muted-foreground/70 mt-0.5 line-clamp-1">
                {t.description}
              </p>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </div>
      </button>
    );
  };

  // =========================================================================
  // Render: Template Picker
  // =========================================================================

  // No trust selected
  if (!selectedTrust?.trust_id) {
    return (
      <div className="main-layout">
        <Sidebar />
        <main className="main-content dot-grid">
          <div className="page-container">
            <div className="flex flex-col items-center justify-center py-20">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="font-serif text-xl text-navy">Select a Trust</h2>
              <p className="text-muted-foreground mt-2">
                Choose a trust from the sidebar to create minutes.
              </p>
            </div>
          </div>
        </main>
        <MobileBottomNav />
      </div>
    );
  }

  const categorized = buildCategorizedTemplates();

  return (
    <div className="main-layout">
      <Sidebar />
      <main className="main-content dot-grid">
        <div className="page-container">
          {/* Back to minutes list */}
          <button
            onClick={() => navigate('/minutes')}
            className="text-sm text-muted-foreground hover:text-navy flex items-center gap-1 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Minutes
          </button>

          {/* Money prefill banner */}
          {showPrefillBanner && (
            <div className="rounded border border-gold/30 bg-gold/10 p-4 text-sm text-navy flex items-start gap-3 mb-6">
              <Sparkles className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Action from Money</span>{' '}
                — This action is being drafted from Money. Ask Trust Assistant to complete it.
                <button
                  onClick={() => navigate('/trust-assistant?prompt=I+need+to+document+a+recent+meeting')}
                  className="ml-1 text-gold hover:text-navy/60 font-semibold underline underline-offset-2"
                >
                  Go to Trust Assistant →
                </button>
              </div>
            </div>
          )}

          {/* Page header */}
          <div className="mb-8">
            <h1 className="font-serif text-2xl text-navy">What are you documenting?</h1>
            <p className="text-muted-foreground mt-1">
              Choose a template to get started, or ask Trust Assistant for help.
            </p>
          </div>

          {/* Onboarding welcome message */}
          {fromOnboarding && (
            <div className="rounded border border-gold/30 bg-gold/10 p-4 text-sm text-navy flex items-start gap-2 mb-6">
              <Sparkles className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Welcome!</span>{' '}
                Let's create your first meeting minutes. Choose a template below or ask Trust Assistant.
              </div>
            </div>
          )}

          {/* Template grid sections */}
          {CATEGORY_ORDER.map((cat) => {
            const templates = categorized[cat.key];
            if (!templates || templates.length === 0) return null;

            return (
              <section key={cat.key} className="space-y-3 mb-10">
                <h2 className="font-serif text-lg text-navy border-b border-gold/20 pb-1">
                  {cat.label}
                  {cat.key === 'recommended' && (
                    <span className="ml-2 text-xs font-mono text-gold normal-case">
                      — most common for your trust
                    </span>
                  )}
                </h2>
                {templatesLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Array.from({ length: templates.length || 4 }).map((_, i) => (
                      <div key={i} className="card-trust animate-pulse p-5 h-24" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {templates.map((t) => renderTemplateCard(t))}
                  </div>
                )}
              </section>
            );
          })}

          {/* Ask Trust Assistant card */}
          {!templatesLoading && (
            <section className="space-y-3 mb-10">
              <h2 className="font-serif text-lg text-navy border-b border-gold/20 pb-1">
                AI-Assisted
              </h2>
              <button
                onClick={() => navigate('/trust-assistant?prompt=I+need+to+document+a+recent+meeting')}
                className="card-trust group relative flex flex-col items-start gap-2 p-5 text-left transition-all hover:border-gold/50 hover:shadow-md w-full md:w-2/3 lg:w-1/2"
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded bg-gold/10 text-gold">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-sm font-semibold text-navy">
                      Ask Trust Assistant
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Describe your meeting in plain English. Trust Assistant transforms your notes into formal, compliant minutes — no legal jargon needed.
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </div>
              </button>
            </section>
          )}

          {/* Info box */}
          <div className="rounded border border-gold/20 bg-gold/5 p-4 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-gold flex-shrink-0" />
              <span>
                Templates guide you through common trustee decisions. New here?
                Start with &ldquo;Initial Trustee Meeting&rdquo; — it walks you through everything.
                Or choose &ldquo;Ask Trust Assistant&rdquo; and describe your meeting in plain language.
              </span>
            </p>
          </div>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
}